/**
 * Server CAD List Component
 * Displays list of CAD/DXF files from server for selection
 */

import { useState, useEffect } from 'react'
import { FileStorageService } from '../../../services/FileStorageService'
import { FileInfo } from '../../../models/FileStorage'
import { useCsvRuntimeStore } from '../model/csvStore'
import styles from './ServerFileList.module.css'

interface ServerCADListProps {
  projectId: number
  mapId?: number
  onFileSelect: (file: FileInfo) => void
  onCancel: () => void
  /** 직접 업로드 화면으로 전환하는 콜백 — 파일이 있어도/없어도 항상 노출. */
  onUploadClick?: () => void
}

const PAGE_SIZE = 5

export function ServerCADList({ projectId, mapId, onFileSelect, onCancel, onUploadClick }: ServerCADListProps) {
  const [files, setFiles] = useState<FileInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    loadFiles(page)
  }, [projectId, page])

  const loadFiles = async (targetPage: number) => {
    try {
      setLoading(true)
      setError(null)
      // 재조회 중엔 "목록 준비됨" 신호를 지워 가이드 카드가 낡은 상태로 계속
      // 비켜있지 않게 한다 — 실제 결과는 아래에서 다시 반영.
      if (targetPage === 1) useCsvRuntimeStore.getState().setDxfListReady(false)

      // Files live at the flat {projectId}/uploads/cad/ path — mapId is intentionally ignored.
      const response = await FileStorageService.listFiles(projectId, 'cad', targetPage, PAGE_SIZE)

      const fetchedFiles = response.data?.files || []
      setFiles(fetchedFiles)
      setTotalCount(response.data?.total_count || 0)
      setTotalPages(response.data?.pagination?.total_pages || 0)
      // 파일이 있든 없든 사용자가 직접 고르거나 업로드를 눌러야 하므로 목록이
      // 뜨는 즉시 "준비됨"으로 알린다 — 가이드 카드가 이 순간 자동으로 비켜준다.
      if (targetPage === 1) useCsvRuntimeStore.getState().setDxfListReady(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'CAD 파일 목록을 불러오지 못했습니다.'
      console.error('[ServerCADList] Error loading CAD files:', errorMessage)
      setError(errorMessage)
      // 에러 화면도 사용자 조치(뒤로)가 필요하니 가이드 카드를 비켜준다.
      if (targetPage === 1) useCsvRuntimeStore.getState().setDxfListReady(true)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1)
  }

  const handleFileClick = (file: FileInfo) => {
    setSelectedFile(file)
  }

  const handleSelectClick = () => {
    if (selectedFile) {
      onFileSelect(selectedFile)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 DXF 파일</h2>
            <p className={styles.description}>파일 목록 불러오는 중...</p>
          </div>
          <div className={styles.spinner} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.overlay} onClick={onCancel}>
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 DXF 파일</h2>
            <p className={styles.error}>{error}</p>
          </div>
          <div className={styles.actions}>
            <button
              className={styles.cancelButton}
              onClick={onCancel}
            >
              뒤로
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (files.length === 0) {
    return (
      <div className={styles.overlay} onClick={onCancel}>
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 DXF 파일</h2>
          </div>
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <p className={styles.emptyTitle}>DXF 파일이 없습니다</p>
            <p className={styles.emptyDescription}>DXF 파일을 업로드해주세요</p>
          </div>
          <div className={styles.actions}>
            <button
              className={styles.cancelButton}
              onClick={onCancel}
            >
              뒤로
            </button>
            {onUploadClick && (
              <button className={styles.selectButton} onClick={onUploadClick}>
                DXF 업로드
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const isSelected = (file: FileInfo) => selectedFile?.filename === file.filename

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>서버 DXF 파일</h2>
          <p className={styles.description}>데이터를 추출할 DXF/CAD 파일을 선택하세요</p>
        </div>

        <div className={styles.fileList}>
          {files.map((file) => (
            <button
              key={file.filename}
              className={`${styles.fileItem} ${isSelected(file) ? styles.selected : ''}`}
              onClick={() => handleFileClick(file)}
            >
              <svg className={styles.fileIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M9 15h6" />
                <path d="M12 18v-6" />
              </svg>
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{file.original_name}</p>
                <div className={styles.fileMeta}>
                  <span className={styles.fileSize}>{formatFileSize(file.size_bytes)}</span>
                  <span className={styles.fileDate}>{formatDate(file.upload_date)}</span>
                </div>
              </div>
              {isSelected(file) && (
                <svg className={styles.checkmark} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={handlePrevPage}
              disabled={page <= 1}
              aria-label="이전 페이지"
            >
              ‹
            </button>
            <span className={styles.pageInfo}>
              {page} / {totalPages}
              <span className={styles.totalCount}> · 총 {totalCount}개</span>
            </span>
            <button
              className={styles.pageButton}
              onClick={handleNextPage}
              disabled={page >= totalPages}
              aria-label="다음 페이지"
            >
              ›
            </button>
          </div>
        )}

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onCancel}
          >
            뒤로
          </button>
          {onUploadClick && (
            <button className={styles.cancelButton} onClick={onUploadClick}>
              새 파일 업로드
            </button>
          )}
          <button
            className={styles.selectButton}
            onClick={handleSelectClick}
            disabled={!selectedFile}
          >
            파일 선택
          </button>
        </div>
      </div>
    </div>
  )
}
